import { useState, useEffect, useRef } from "react";
import {
    CloudUpload,
    TrashBinMinimalistic,
    DocumentText,
} from "@solar-icons/react/ssr";
import { useTheme, Button, IconButton, Grid } from "@mui/material";
import Alert from "@mui/material/Alert";
import CircularProgress from "@mui/material/CircularProgress";
import InputLabel from "@mui/material/InputLabel";
import Link from "@mui/material/Link";
import Snackbar from "@mui/material/Snackbar";

import { useDispatch, useSelector } from "react-redux";

import { request } from "@/Api";
import {
    FileUploadContainer,
    FileUploadFiles,
} from "@/common/StyledComponents";

import { SectionTitle } from "@/common/typography";

import { setError } from "@/nav/navSlice";

export function FilesStore({
    filesData,
    title,
    innerTitle,
    acceptedFormats,
    disabled,
    single,
    externalFileList,

    onFilesTransfer,
    onFileDelete,

    isFilesProcessing,
}) {
    const fileHolder = useRef();
    const theme = useTheme();

    const [files, setFiles] = useState([]);
    const [isDropOver, setIsDropOver] = useState(false);
    const [showInvalidFileAlert, setShowInvalidFileAlert] = useState(false);

    const uploadDisabled = disabled || (single && files.length > 0);

    useEffect(() => {
        if (!filesData) {
            return;
        }
        setFiles(filesData);
    }, [filesData]);

    const getDataTransferFiles = (dataTransfer, single) => {
        const transferFiles = [];
        const items = [...dataTransfer.items];
        for (const i in items) {
            const item = items[i];
            const file = item.getAsFile();
            transferFiles.push(file);
            if (single) {
                break;
            }
        }
        return transferFiles;
    };

    const uploadButtonClickHandler = () => {
        fileHolder.current.click();
    };

    const onTryUploadHandler = (e) => {
        try {
            onUploadHandler(e);
        } catch (error) {
            setShowInvalidFileAlert(true);
        } finally {
            fileHolder.current.value = null;
        }
    };

    const onUploadHandler = (e) => {
        e.preventDefault();

        if (uploadDisabled) {
            return;
        }

        setIsDropOver(false);

        let dataTransfer = new DataTransfer();

        if (!e.dataTransfer) {
            for (var i in fileHolder.current.files) {
                if (fileHolder.current.files[i].constructor.name == "File") {
                    dataTransfer.items.add(fileHolder.current.files[i]);
                }
            }
        } else {
            dataTransfer = e.dataTransfer;
        }

        for (i in Array.from(dataTransfer.files)) {
            const file = dataTransfer.files[i];
            const extention = file.name.split(".").pop().toLowerCase();
            if (!acceptedFormats.includes(`.${extention}`)) {
                setShowInvalidFileAlert(true);
                return;
            }
        }

        const transferFiles = getDataTransferFiles(dataTransfer, single);

        if (onFilesTransfer) {
            onFilesTransfer(transferFiles);
        }
    };

    const onDropOverHandler = (e) => {
        e.preventDefault();
        if (uploadDisabled) {
            return;
        }
        setIsDropOver(true);
    };
    const onDropLeaveHandler = (e) => {
        if (uploadDisabled) {
            return;
        }
        setIsDropOver(false);
    };

    const onFileDeleteHandler = (file, storeIndex) => (e) => {
        onFileDelete && onFileDelete(file, storeIndex);
        setFiles(files.filter((_, index) => index != storeIndex));
    };

    const FileItem = ({ file, index, externalFileList }) => {
        const style = externalFileList
            ? {
                  borderRadius: 1,
                  background: "transparent",
                  justifyContent: "space-between",
                  width: "100%",
                  marginTop: "16px",
              }
            : {
                  borderRadius: 1,
                  background: "#ffffff22",
                  margin: 1,
                  justifyContent: "space-between",
                  width: "100%",
                  padding: 1,
              };
        return (
            <Grid alignItems={"center"} wrap="nowrap" container sx={style}>
                <Grid item sx={{ overflow: "auto" }}>
                    <Grid container alignItems={"center"} wrap="nowrap">
                        <Grid item padding={0}>
                            <DocumentText style={{ marginRight: 16 }} />
                        </Grid>
                        <Grid item padding={0}>
                            <InputLabel>
                                {file.url ? (
                                    <Link target={"_blank"} href={file.url}>
                                        {file.name}
                                    </Link>
                                ) : (
                                    <span style={{ textOverflow: "ellipsis" }}>
                                        {file.name}
                                    </span>
                                )}
                            </InputLabel>
                        </Grid>
                    </Grid>
                </Grid>
                <Grid item>
                    {disabled ? null : (
                        <IconButton onClick={onFileDeleteHandler(file, index)}>
                            <TrashBinMinimalistic />
                        </IconButton>
                    )}
                </Grid>
            </Grid>
        );
    };

    return (
        <>
            {title && <SectionTitle>{title}</SectionTitle>}
            <FileUploadContainer
                borderColor={
                    isDropOver
                        ? theme.palette.text.primary
                        : theme.palette.text.secondary
                }
                borderStyle={isDropOver || uploadDisabled ? "solid" : "dashed"}
            >
                <div
                    style={{ width: "100%" }}
                    onDragLeave={onDropLeaveHandler}
                    onDragOver={onDropOverHandler}
                    onDrop={onTryUploadHandler}
                >
                    {externalFileList ? (
                        <>
                            {uploadDisabled ? (
                                <InputLabel>None</InputLabel>
                            ) : isFilesProcessing ? (
                                <InputLabel>
                                    Uploading your file(s)... wait a moment
                                </InputLabel>
                            ) : (
                                <InputLabel>
                                    {innerTitle ||
                                        "Drag & drop documents (PDF or image)"}
                                </InputLabel>
                            )}
                        </>
                    ) : (
                        <>
                            {files.length > 0 ? (
                                files.map((file, index) => {
                                    return (
                                        <FileItem
                                            key={index}
                                            index={index}
                                            file={file}
                                        />
                                    );
                                })
                            ) : (
                                <>
                                    {uploadDisabled ? (
                                        <InputLabel>None</InputLabel>
                                    ) : isFilesProcessing ? (
                                        <InputLabel>
                                            Uploading your file(s)... wait a
                                            moment
                                        </InputLabel>
                                    ) : (
                                        <InputLabel>
                                            {innerTitle ||
                                                "Drag & drop documents (PDF or image)"}
                                        </InputLabel>
                                    )}
                                </>
                            )}
                        </>
                    )}

                    {uploadDisabled ? null : isFilesProcessing ? (
                        <CircularProgress />
                    ) : (
                        <Button onClick={uploadButtonClickHandler}>
                            <CloudUpload style={{ marginRight: 8 }} />
                            Upload
                        </Button>
                    )}
                </div>
            </FileUploadContainer>

            {externalFileList && files.length > 0 && (
                <FileUploadFiles
                    backgroundColor={theme.palette.background.paper}
                >
                    <h3>Documents</h3>
                    {files.map((file, index) => (
                        <FileItem
                            key={index}
                            index={index}
                            file={file}
                            externalFileList
                        />
                    ))}
                </FileUploadFiles>
            )}

            <input
                accept={acceptedFormats.join(",")}
                ref={fileHolder}
                style={{ display: "none" }}
                type="file"
                id="files"
                multiple={!single}
                onChange={onTryUploadHandler}
            />

            <Snackbar
                open={showInvalidFileAlert}
                autoHideDuration={2000}
                onClose={(event, reason) => {
                    setShowInvalidFileAlert(false);
                }}
            >
                <Alert severity="error">
                    Only {acceptedFormats.join(", ")} files are accepted
                </Alert>
            </Snackbar>
        </>
    );
}

export default function FilesUploader({
    files,
    modelType,
    modelId,
    title,
    disabled,
    single,
    onUploadedCallback,
    acceptedFormats = [".pdf", ".png", ".jpeg", ".docx", ".txt", ".csv"],
}) {
    const [filesLocal, setFilesLocal] = useState(files);
    const [isFilesUploading, setIsFilesUploading] = useState(false);
    const dispatch = useDispatch();

    const onFileDeleteHandler = (file, index) => {
        const fileId = file.id;
        request.delete(`files/${fileId}`);
        setFilesLocal((prev) => prev.filter((_, i) => i !== index));
    };

    const handleFilesTransfer = (transferFiles) => {
        const newFiles = [];
        const promises = [];
        setIsFilesUploading(true);
        transferFiles.forEach((file) => {
            const formData = new FormData();
            formData.append("file", file, file.name);
            formData.append("model_type", modelType);
            formData.append("model_instance_id", modelId);
            promises.push(
                request.post(`/files`, formData).then((response) => {
                    newFiles.push(response.data);
                }),
            );
        });

        Promise.all(promises)
            .then(() => {
                onUploadedCallback && onUploadedCallback(newFiles);
                setFilesLocal((prev) => [...prev, ...newFiles]);
            })
            .catch((error) => {
                dispatch(setError({ message: error.response.data.detail }));
            })
            .finally(() => {
                setIsFilesUploading(false);
            });
    };

    return (
        <FilesStore
            title={title}
            filesData={filesLocal}
            acceptedFormats={acceptedFormats}
            onFilesTransfer={handleFilesTransfer}
            onFileDelete={onFileDeleteHandler}
            disabled={disabled}
            single={single}
            isFilesProcessing={isFilesUploading}
        />
    );
}
